import React from 'react';
import { ArrowRight } from 'lucide-react';
import TurnstileWidget from '@/components/common/TurnstileWidget';
import type { FormShield } from '@/hooks/useFormShield';

interface ContactDetails {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
  services: string[];
  primaryGoal: string;
  smsConsent: boolean;
}

interface Props {
  data: ContactDetails;
  errors: Record<'name' | 'email' | 'phone' | 'message' | 'smsConsent', string>;
  shield: FormShield;
  submitting: boolean;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onStart: () => void;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onService: (_service: string) => void;
  onGoal: (_goal: string) => void;
  onConsent: (_checked: boolean) => void;
}

const services = [
  ['AGENTS', 'AI agents'],
  ['ADS', 'Advertising'],
  ['AUTOMATION', 'Automation'],
  ['WEBSITE', 'Website'],
] as const;
const goals = [
  ['more_leads', 'More qualified leads'],
  ['missed_calls', 'Miss fewer calls'],
  ['better_website', 'Better website'],
  ['automate_follow_up', 'Automate follow-up'],
] as const;

const SinglePageContactForm = ({
  data,
  errors,
  shield,
  submitting,
  onSubmit,
  onStart,
  onChange,
  onService,
  onGoal,
  onConsent,
}: Props) => {
  const field = (
    name: 'name' | 'email' | 'phone' | 'company',
    label: string,
    type: string,
    placeholder: string,
    autoComplete: string,
    required = false
  ) => (
    <div className="min-w-0">
      <label
        htmlFor={`contact-page-${name}`}
        className="mb-2 block text-sm font-medium text-white/90"
      >
        {label}
        {required && (
          <span className="ml-1 text-elev8-blue" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <input
        id={`contact-page-${name}`}
        name={name}
        type={type}
        autoComplete={autoComplete}
        required={required}
        value={data[name]}
        onChange={onChange}
        placeholder={placeholder}
        aria-invalid={name !== 'company' && !!errors[name]}
        aria-describedby={
          name !== 'company' && errors[name]
            ? `contact-page-${name}-error`
            : undefined
        }
        className={`input-field min-h-12 ${name !== 'company' && errors[name] ? 'border-red-400' : ''}`}
      />
      {name !== 'company' && errors[name] && (
        <p
          id={`contact-page-${name}-error`}
          className="mt-2 text-sm text-red-400"
          role="alert"
        >
          {errors[name]}
        </p>
      )}
    </div>
  );

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      onFocusCapture={onStart}
      className="space-y-5"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {field('name', 'Name', 'text', 'Your name', 'name', true)}
        {field(
          'email',
          'Email',
          'email',
          'you@yourbusiness.com',
          'email',
          true
        )}
      </div>
      {field('phone', 'Phone', 'tel', '(978) 555-0123', 'tel', true)}
      <div>
        <label
          htmlFor="contact-page-message"
          className="mb-2 block text-sm font-medium text-white/90"
        >
          What would you like help with?{' '}
          <span className="font-normal text-white/50">(optional)</span>
        </label>
        <textarea
          id="contact-page-message"
          name="message"
          rows={3}
          value={data.message}
          onChange={onChange}
          className="input-field resize-y"
          placeholder="Missed calls, more leads, a better website—start with what's on your mind."
        />
      </div>
      <details className="rounded-xl border border-white/10 px-4 py-3">
        <summary className="cursor-pointer text-sm font-medium text-white/70 focus-visible:outline-elev8-blue">
          Add business details{' '}
          <span className="font-normal text-white/50">(optional)</span>
        </summary>
        <div className="mt-5 space-y-5">
          {field(
            'company',
            'Company',
            'text',
            'Your business name',
            'organization'
          )}
          <fieldset>
            <legend className="mb-3 text-sm text-white/80">
              Services you’re interested in
            </legend>
            <div className="grid grid-cols-2 gap-2">
              {services.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={data.services.includes(value)}
                  onClick={() => onService(value)}
                  className={`min-h-11 rounded-lg border px-3 py-2 text-sm transition-colors ${data.services.includes(value) ? 'border-elev8-blue bg-elev8-blue/10 text-elev8-blue' : 'border-white/10 text-white/70 hover:border-white/40'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="mb-3 text-sm text-white/80">
              What matters most right now?
            </legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {goals.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={data.primaryGoal === value}
                  onClick={() => onGoal(value)}
                  className={`min-h-11 rounded-lg border px-3 py-2 text-sm transition-colors ${data.primaryGoal === value ? 'border-elev8-blue bg-elev8-blue/10 text-elev8-blue' : 'border-white/10 text-white/70 hover:border-white/40'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>
        </div>
      </details>
      <div>
        <label
          htmlFor="contact-page-smsConsent"
          className="flex cursor-pointer items-start gap-3 text-sm leading-snug text-white/70"
        >
          <input
            id="contact-page-smsConsent"
            type="checkbox"
            name="smsConsent"
            checked={data.smsConsent}
            onChange={event => onConsent(event.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-elev8-blue"
          />
          <span>
            I agree to receive SMS messages from ELEV8 Growth Solutions{' '}
            <span className="text-white/50">(optional)</span>
          </span>
        </label>
        <p className="mt-2 pl-7 text-xs leading-relaxed text-white/50">
          By checking this box, you agree to receive SMS messages from ELEV8
          Growth Solutions. Message and data rates may apply. Reply STOP to
          unsubscribe.
        </p>
      </div>
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        {...shield.honeypot}
      />
      <TurnstileWidget
        onVerify={shield.onTurnstileVerify}
        onUnavailable={shield.onTurnstileUnavailable}
        resetSignal={shield.resetSignal}
        className="flex justify-center"
      />
      {shield.turnstileState === 'unavailable' && (
        <p className="text-xs text-white/60">
          Having trouble with the security check? Email{' '}
          <a
            href="mailto:info@elev8growth.solutions"
            className="break-all text-elev8-blue underline"
          >
            info@elev8growth.solutions
          </a>{' '}
          or call us.
        </p>
      )}
      <button
        type="submit"
        disabled={submitting || !shield.ready}
        className="btn-primary min-h-12 w-full disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? 'Sending…' : 'Start the conversation'}
        {!submitting && <ArrowRight size={18} aria-hidden="true" />}
      </button>
    </form>
  );
};

export default SinglePageContactForm;
